/** @jsxImportSource @emotion/react */
import { createTheme } from "@mui/material/styles";

// Define the Custom Theme for Light & Dark Mode
const customTheme = createTheme({
  /*  cssVariables: {
    colorSchemeSelector: "data-toolpad-color-scheme",
  }, */
  colorSchemes: {
    light: {
      palette: {
        background: {
          default: "#F9F9FE",
          paper: "#EEEEF9",
        },
        text: {
          primary: "#1E1E1E",
          secondary: "#6B7280",
        },
        divider: "#E0E0E0",
        action: {
          hover: "#F5F5F5",
          selected: "#E0E7FF",
        },
        primary: {
          main: "#3F51B5", // Classic MUI dark blue
          contrastText: "#FFFFFF",
        },
      },
    },
    dark: {
      palette: {
        background: {
          default: "#1C2A42", // Deep blue for a refined dark mode
          paper: "#112E4D",
        },
        text: {
          primary: "#FFFFFF",
          secondary: "#A0AEC0",
        },
        divider: "#3d4d60",
        action: {
          hover: "#2D3A55",
          selected: "#2D3748",
        },
        primary: {
          main: "#90CAF9", // Lighter blue for readability in dark mode
          contrastText: "#1C1C1C",
        },
      },
    },
  },
});

// ** Shared Select Styles for Light & Dark Mode **
const getCommonSelectStyles = (theme) => ({
  control: (base, state) => ({
    ...base,
    width: "100%",
    minHeight: "56px", // Matches MUI TextField height
    borderRadius: "4px", // Matches MUI border radius
    borderWidth: state.isFocused ? "2px" : "1px",
    borderColor: state.isFocused
      ? theme.palette.primary.main
      : theme.palette.grey[400], // Matches MUI default border

    backgroundColor: theme.palette.background.paper,
    boxShadow: state.isFocused
      ? `0 0 0 2px ${theme.palette.primary.main}40` // Subtle glow like MUI focus
      : "none",
    "&:hover": {
      borderColor: theme.palette.primary.main,
    },
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: theme.palette.text.primary,
    padding: "8px",
    "&:hover": {
      color: theme.palette.primary.main,
    },
    svg: { width: "20px", height: "20px" },
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: theme.palette.background.paper,
    borderRadius: "8px",
    boxShadow: "0px 4px 12px rgba(0,0,0,0.15)", // Matches MUI Paper shadow

    padding: "8px 0",
    zIndex: 9999,
    position: "absolute", // Keeps menu separate from surrounding elements
    overflow: "hidden", // Prevents content bleeding
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: "220px",
    padding: "4px",
    backgroundColor: theme.palette.background.paper,
    borderRadius: "8px",
    zIndex: 9999,
  }),
  option: (base, { isFocused, isSelected }) => ({
    ...base,
    backgroundColor: isSelected
      ? theme.palette.action.selected
      : isFocused
        ? theme.palette.action.hover
        : "transparent",
    color: theme.palette.text.primary,
    cursor: "pointer",
    padding: "10px 16px",
    transition: "background 0.2s ease-in-out",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  }),
  placeholder: (base) => ({
    ...base,
    color: theme.palette.text.secondary,
    fontSize: "1rem",
  }),
  singleValue: (base) => ({
    ...base,
    color: theme.palette.text.primary,
    fontSize: "1rem",
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: theme.palette.action.selected,
    borderRadius: "16px",
    padding: "4px 8px",
    display: "flex",
    alignItems: "center",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: theme.palette.text.primary,
    fontSize: "0.875rem",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: theme.palette.text.secondary,
    cursor: "pointer",
    "&:hover": {
      color: theme.palette.primary.main,
    },
  }),
});

// ** Separate Single & Multi Select Styles **
const getSingleSelectStyles = (theme) => ({
  ...getCommonSelectStyles(theme),
});

const getMultiSelectStyles = (theme) => ({
  ...getCommonSelectStyles(theme),
  multiValue: (base) => ({
    ...base,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: theme.palette.primary.contrastText,
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: theme.palette.primary.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  }),
});

// ** Exported Components **
export { customTheme, getSingleSelectStyles, getMultiSelectStyles };
